// web-staking-estimator-frontend/src/EstimatorTool.js
import React, { useState, useEffect, useCallback } from "react";
import MapComponent from "./MapComponent";
import EstimateResults from "./EstimateResults";
import ServiceTypeSelector from "./ServiceTypeSelector";
import UserRequestForm from "./UserRequestForm";
import RequestAssistance from "./RequestAssistance";
import { StepBadge, LoadingOverlay, FormField } from "./components/common";
import { useUIConfig } from "./hooks/useUIConfig";
import { useMemberInfo } from "./hooks/useMemberInfo";
import { useServiceRequest } from "./hooks/useServiceRequest";
import { useEstimateState } from "./hooks/useEstimateState";
import { useSubmitActions } from "./hooks/useSubmitActions";
import { useEstimateComputation } from "./hooks/useEstimateComputation";
import { useUIVisibility } from "./hooks/useUIVisibility";
import { useAppReset } from "./hooks/useAppReset";
import { useWizardScrollBehavior } from "./hooks/useWizardScrollBehavior";
import "./EstimatorTool.css";

// --- Debug gate: suppress our namespaced logs unless window.DEBUG_STAKING === true
// Namespaces we use in logs: [URF:], [MC:], [APP:], [admin-compute]
(function installStakingLogGate() {
  try {
    const TAG = /^\[(URF|MC|APP|admin-compute)\:/; // only filter our own logs
    const on = () => {
      try {
        return !!window.DEBUG_STAKING;
      } catch {
        return false;
      }
    };
    ["log", "warn"].forEach((fn) => {
      const orig = console[fn] && console[fn].bind(console);
      if (!orig) return;
      console[fn] = (...args) => {
        const first = args[0];
        if (typeof first === "string" && TAG.test(first) && !on()) return;
        return orig(...args);
      };
    });
  } catch (_) {
    /* no-op */
  }
})();

function EstimatorTool() {
  const [serviceType, setServiceType] = useState(null);
  const [estimateErr, setEstimateErr] = useState(null);
  const [submitMsg, setSubmitMsg] = useState(null);
  const [isSearchingMap, setIsSearchingMap] = useState(false);
  const [resetSignal, setResetSignal] = useState(0);

  const [selectedPoint, setSelectedPoint] = useState({
    latitude: null,
    longitude: null,
    x: null,
    y: null,
    wkid: null,
  });

  // Nearest-derived hints (we only use xfmrOHUG downstream)
  const [infraHints, setInfraHints] = useState({
    xfmrOHUG: null,
    infraPhase: null,
  });

  // Use custom hooks
  const { uiCfg } = useUIConfig();
  const {
    member,
    setMember,
    handlePhoneChange,
    formattedPhone,
    memberOk,
    getMissingForRequest,
  } = useMemberInfo({ defaultState: uiCfg?.ui?.memberDefaults?.state || "OR" });

  const {
    requestInfo,
    setRequestInfo,
    serviceOk,
    getMissingForEstimate,
  } = useServiceRequest();

  const {
    estimate,
    setEstimate,
    estimateId,
    setEstimateId,
    prettyEstimateId,
    currentSig,
    lastSigForId,
    setLastSigForId,
    ensureSaved,
  } = useEstimateState({ requestInfo, selectedPoint, member, serviceType });

  const {
    isGenerating,
    isSubmitting,
    handleGenerateEstimate,
    handleStartServiceRequest,
  } = useSubmitActions({
    ensureSaved,
    member,
    prettyEstimateId,
    getMissingForEstimate: () => getMissingForEstimate(selectedPoint),
    getMissingForRequest,
    uiCfg,
    setSubmitMsg,
    setEstimateErr,
    setEstimate,
  });

  // Automatic estimate computation
  useEstimateComputation({
    serviceType,
    selectedPoint,
    requestInfo,
    setEstimate,
    setRequestInfo,
  });

  // UI visibility logic
  const {
    hasPin,
    estimateVisible,
    nearestNone,
    assistanceVisible,
    hasAnyInput,
  } = useUIVisibility({
    selectedPoint,
    serviceOk,
    memberOk,
    infraHints,
    requestInfo,
    isSearchingMap,
  });

  // Reset/Start Over functionality
  const { handleStartOver } = useAppReset({
    requestInfo,
    setRequestInfo,
    setSelectedPoint,
    setEstimate,
    setResetSignal,
    setSubmitMsg,
    setEstimateErr,
  });

  const categoryMap = {
    Residential: "residential",
    Irrigation: "irrigation",
    Commercial: "commercial",
  };
  const category = categoryMap[serviceType] || "residential";

  // When we first land on this screen, initialize svc_type from the prior screen choice.
  // Do this once (only when it's currently null), so we don't fight later updates.
  useEffect(() => {
    if (!serviceType) return; // no category chosen yet
    if (requestInfo.svc_type != null) return; // already initialized; don't touch
    setRequestInfo((prev) => ({
      ...prev,
      svc_type: serviceType, // e.g., "Irrigation" / "Commercial" / "Residential"
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceType]);

  const handleNearestSummary = useCallback((h) => {
    setInfraHints(h || { xfmrOHUG: null, infraPhase: null });
  }, []);

  // Wrap setEstimate to avoid recreating on every render
  const handleSetEstimate = useCallback((nextOrObj) => {
    if (typeof nextOrObj === "function") {
      setEstimate((prev) => {
        const maybe = nextOrObj(prev) || prev;
        return {
          ...prev,
          mapImageDataUrl:
            maybe.mapImageDataUrl ?? prev.mapImageDataUrl,
        };
      });
    } else if (nextOrObj && typeof nextOrObj === "object") {
      setEstimate((prev) => ({
        ...prev,
        mapImageDataUrl:
          nextOrObj.mapImageDataUrl ?? prev.mapImageDataUrl,
      }));
    }
  }, []);

  // Clear estimateId if signature changes
  useEffect(() => {
    if (estimateId && lastSigForId && currentSig !== lastSigForId) {
      setEstimateId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSig]);

  // Clear success/error banner when the user changes request inputs or contact info
  useEffect(() => {
    if (submitMsg) setSubmitMsg(null);
  }, [
    // request selections
    requestInfo.svc_phase,
    requestInfo.svc_volts,
    requestInfo.svc_amps,
    requestInfo.svc_ohug,
    requestInfo.matched_service_id,
    // contact fields
    member.first_name,
    member.last_name,
    member.phone,
    member.email,
    // pin (so changing the location also clears)
    selectedPoint.x,
    selectedPoint.y,
    selectedPoint.wkid,
    // service category (Residential/Irrigation/Commercial)
    serviceType,
  ]);

  // Auto-scroll behavior when sections are revealed
  const { reqDetailsRef, estimateRef } = useWizardScrollBehavior(hasPin, estimateVisible);


  if (!serviceType) {
    return <ServiceTypeSelector onSelect={setServiceType} uiCfg={uiCfg} />;
  }

  return (
    <div className="appContainer">
      <div className="panel card instructionsCard">
        <div className="instructions-header-row">
          <div className="instructions-header-left">
            <h2 className="sectionTitle" style={{ marginBottom: 6 }}>
              Request for{" "}
              <span style={{ textTransform: "capitalize" }}>{category}</span>{" "}
              service
            </h2>
            <ul className="stepsList" style={{ listStyle: "none", paddingLeft: 0, margin: 0 }}>
              <li>
                <StepBadge n={1} /> <b>Select Location</b> — Click the map to drop a
                pin, or enter an address in the search bar, to indicate where you
                are requesting your new service.
              </li>
              <li>
                <StepBadge n={2} /> <b>Enter details</b> — Provide details about
                your new service location and the type of service you are
                requesting.
              </li>
              <li>
                <StepBadge n={3} /> <b>Review results</b> — Click{" "}
                <i>Generate My Estimate</i> to review your results Click{" "}
                <i>Start Service Request</i> to start the new service connection
                process.
              </li>
            </ul>
          </div>
          <div className="instructions-header-right">
            <img
              src="/themes/default/branding/logo-compact.png"
              alt="Service Estimator"
              className="instructions-header-logo"
            />
            <button
              type="button"
              className={`btn btn-secondary${hasAnyInput ? '' : ' invisible'}`}
              onClick={handleStartOver}
              disabled={!hasAnyInput || isGenerating || isSubmitting}
              aria-label="Start over and clear current inputs"
            >
              Start over
            </button>
          </div>
        </div>
      </div>

      <div className="panel card leftPanel">
        <h2>
          <StepBadge n={1} /> Select New Service Location on Map
        </h2>
        <MapComponent
          serviceType={category}
          setEstimate={handleSetEstimate}
          setSelectedPoint={setSelectedPoint}
          onNearestSummary={handleNearestSummary}
          onSearchingChange={setIsSearchingMap}          
          requiredPhase={requestInfo?.svc_phase ?? null}
          resetSignal={resetSignal}
          isGeneratingOrSubmitting={isGenerating || isSubmitting}
          generatingMessage={isGenerating ? "Calculating…" : "Submitting request…"}
        />
      </div>

      {/* RIGHT PANEL (render only when there is content) */}
      {(hasPin || !!requestInfo?.blocked || estimateVisible) && (
        <div className="panel card rightPanel">
          {/* generating overlay */}
          {(isGenerating || isSubmitting) && (
            <LoadingOverlay
              message={isGenerating ? "Calculating…" : "Submitting request…"}
              ariaLabel={isGenerating ? "Generating estimate" : "Submitting request"}
            />
          )}

          {/* everything below MUST stay inside rightBody to keep the outer card tall */}
          <div className="rightBody">
            {/* SUB-CARD: Request Assistance (revealed when blocked OR no-nearest) */}
            <RequestAssistance
              visible={assistanceVisible}
              member={member}
              request={requestInfo}
              pin={selectedPoint}
              lastEstimateId={estimateId}
              nearestNone={nearestNone}
            />

            {/* SUB-CARD: Request Details (revealed after point selection) */}
            {hasPin && (
              <div className="subcard">
                <h3 className="subcardTitle" tabIndex={-1} ref={reqDetailsRef}>
                  <StepBadge n={2} /> Request Details
                </h3>
                <UserRequestForm
                  category={category}
                  value={requestInfo}
                  onChange={setRequestInfo}
                  selectedPoint={selectedPoint}
                  xfmrOHUGHint={infraHints.xfmrOHUG}
                  member={member}
                  onMemberChange={setMember}
                  nearestNone={!!infraHints?.noneFound}
                  defaultState={uiCfg?.ui?.memberDefaults?.state || "OR"}
                />
              </div>
            )}

            {/* SUB-CARD: Member Information (revealed after point selection) */}
            {hasPin && (
              <div className="subcard memberInfoCard">
                <h3 className="subcardTitle">Member Information</h3>
                <div className="formGrid">
                  <FormField
                    label="First name"
                    required
                    value={member.first_name}
                    onChange={(e) =>
                      setMember({ ...member, first_name: e.target.value })
                    }
                  />

                  <FormField
                    label="Last name"
                    required
                    value={member.last_name}
                    onChange={(e) =>
                      setMember({ ...member, last_name: e.target.value })
                    }
                  />

                  <FormField
                    label="Business name"
                    value={member.business_name}
                    onChange={(e) =>
                      setMember({ ...member, business_name: e.target.value })
                    }
                  />

                  <FormField
                    label="Telephone"
                    required
                    inputMode="numeric"
                    autoComplete="tel"
                    pattern="\d{10}"
                    placeholder="(555) 123-4567"
                    value={formattedPhone}
                    onChange={handlePhoneChange}
                    ariaLabel="Telephone (10 digits)"
                  />

                  <FormField
                    label="Email"
                    value={member.email}
                    onChange={(e) =>
                      setMember({ ...member, email: e.target.value })
                    }
                  />
                </div>
              </div>
            )}

            {/* SUB-CARD: Estimate (revealed when service + member info are ready) */}
            {estimateVisible && (
              <div className="subcard">
                <h3 className="subcardTitle" tabIndex={-1} ref={estimateRef}>
                  <StepBadge n={3} /> Estimate
                </h3>
                <EstimateResults
                  estimate={estimate}
                  requestInfo={requestInfo}
                  estimateId={estimateId}
                  onGenerateEstimate={handleGenerateEstimate}
                  actionsDisabled={requestInfo?.blocked || isGenerating}
                  onStartServiceRequest={handleStartServiceRequest}
                  onStartOver={handleStartOver}
                  inlineError={estimateErr}
                  bannerMessage={submitMsg ? submitMsg.text : null}
                  bannerKind={submitMsg ? submitMsg.type : null}
                  startRequestDisabled={
                    !estimateId ||
                    isGenerating ||
                    !!requestInfo?.blocked ||
                    getMissingForRequest().length > 0
                  }
                  startOverDisabled={isGenerating || isSubmitting}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default EstimatorTool;
